"use client";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useCallback, useState } from "react";
import { FaBars } from "react-icons/fa";
import { DesktopLink, MobileLink } from "./HeaderLinks";
import { useLinks, type LinkChildren } from "./links";
import { SwitchLanguage } from "../SwitchLanguage";

const DesktopDropdown = ({ children }: { children: LinkChildren[] }) => {
  const pathname = usePathname();

  return (
    <ul className="invisible absolute top-40 rounded bg-white py-5 text-black opacity-0 shadow-xl transition-all duration-300 group-hover:visible group-hover:top-full group-hover:opacity-100 rtl:lg:left-1/3 rtl:xl:left-1/4">
      {children.map((subLink, index) => {
        const isActive = pathname === subLink.url;
        return (
          <li key={index} className="mb-3 font-bold last:mb-0">
            <Link
              href={subLink.url}
              className={`px-10 py-4 text-base font-normal ${
                isActive ? "text-orange" : ""
              } hover:text-orange`}
            >
              {subLink.title}
            </Link>
          </li>
        );
      })}
    </ul>
  );
};

const MobileDropdown = ({
  title,
  children,
  onClose
}: {
  title: string;
  children: LinkChildren[];
  onClose: () => void;
}) => {
  const pathname = usePathname();

  return (
    <li>
    <div className="relative">
      <input type="checkbox" id={title} className="peer hidden" />
      <label
        htmlFor={title}
        className="relative z-10 flex items-center justify-between border-b pb-7 ps-4 font-semibold text-black after:me-4 after:mt-1 after:h-3 after:w-3 after:rotate-45 after:border-b-2 after:border-r-2 after:border-black after:transition-all after:duration-300 peer-checked:after:rotate-223 lg:py-10"
      >
        {title}
      </label>
      <ul className="max-h-0 overflow-hidden transition-all duration-500 ease-in-out peer-checked:max-h-96">
        {children.map((subLink, index) => {
          const isActive = pathname === subLink.url;
          return (
            <li key={index} className="border-b py-5 ps-5">
              <Link
              onClick={onClose}
                href={subLink.url}
                className={`transition-all hover:opacity-80 ${
                  isActive ? "text-orange" : "text-black"
                }`}
              >
                {subLink.title}
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
    </li>
  );
};

const DesktopNav = () => {
  const links = useLinks();
  return (
    <ul className="flex text-base font-semibold lg:gap-10 xl:gap-12 rtl:lg:text-sm rtl:xl:text-base">
      {links.map((link, index) => (
        <DesktopLink key={index} title={link.title} url={link.url}>
          {link.children?.length && (
            <DesktopDropdown>{link.children}</DesktopDropdown>
          )}
        </DesktopLink>
      ))}
    </ul>
  );
};

const MobileNavLinks = ({ onClose }: { onClose: () => void }) => {
  const links = useLinks();
  return links.map((link, index) =>
    link.children?.length ? (
      <MobileDropdown key={index} title={link.title} onClose={onClose}>
        {link.children}
      </MobileDropdown>
    ) : (
      <MobileLink
        key={index}
         onClose={onClose}
        className="relative w-full border-b pb-7 font-semibold transition-all  hover:opacity-80"
        title={link.title}
        url={link.url}
      />
    )
  );
};

const MobileNav = () => {
  const [isOpen, setIsOpen] = useState(false);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleToggle = useCallback(() => {
    setIsOpen((open) => !open);
  }, []);

  return (
    <>
      <nav>
        <button className="relative z-10 cursor-pointer" onClick={handleToggle}>
          <FaBars aria-label="bar" className="mt-2" />
        </button>

        <div
          className={`fixed top-0 z-30 h-full w-3/4 bg-white shadow-2xl transition-transform duration-500 ease-in-out ${
            isOpen
              ? "translate-x-0"
              : "ltr:translate-x-full rtl:-translate-x-full"
          } ${isOpen ? "ltr:right-0 rtl:left-0" : "ltr:right-0 rtl:left-0"}`}
        >
          <div className="flex h-full flex-col overflow-y-auto">
            <div className="flex-1 py-4">
              <ul className="flex flex-col gap-7">
                  <MobileNavLinks onClose={handleClose} />
                <li>
                  <SwitchLanguage className="ps-5 block sm:hidden" />
                </li>
              </ul>
            </div>
          </div>
        </div>
      </nav>
      <div
        onClick={handleClose}
        className={`fixed left-0 top-0 z-20 h-full w-full bg-black transition-opacity duration-500 ${
          isOpen ? "opacity-30" : "pointer-events-none opacity-0"
        }`}
      />
    </>
  );
};

type Props = {
  className?: string;
};

export const Nav: React.FC<Props> = ({ className }) => (
  <div className={className}>
    <div className="hidden lg:flex">
      <DesktopNav />
    </div>
    <div className="lg:hidden">
      <MobileNav />
    </div>
  </div>
);
